/**
 * Admin billing API client — Stripe product, price, subscription, and trial management.
 *
 * All endpoints require admin authentication (Bearer JWT in Authorization header).
 */

import { adminFetch } from './client';
import type {
  BillingPlan,
  FeatureCategory,
  FeatureId,
  FeaturePresentation,
  PlanFeaturePolicy,
  Subscription,
  PlatformFeatureOverride,
} from '@grid2/shared-types';
import type { OrganizationFeaturesResponse } from './org-admin';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface StripePrice {
  id: string;
  productId: string;
  active: boolean;
  currency: string;
  unitAmount: number | null;
  recurring: {
    interval: string;
    intervalCount: number;
  } | null;
  metadata: Record<string, string>;
  created: number;
}

export interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  metadata: Record<string, string>;
  defaultPriceId: string | null;
  prices: StripePrice[];
  created: number;
  updated: number;
}

export interface AdminSubscription extends Subscription {
  tenantName?: string;
  tenantSlug?: string;
}

export interface BillingStats {
  mrr: number;
  totalSubscribers: number;
  activeTrials: number;
  churnRate: number;
  trialConversionRate: number;
}

export interface TrialInfo {
  tenantId: string;
  tenantName?: string;
  daysRemaining: number;
  expiresAt: string;
  planName: string;
}

export interface CreateProductRequest {
  name: string;
  description?: string;
  metadata?: Record<string, string>;
}

export interface UpdateProductRequest {
  name?: string;
  description?: string;
  metadata?: Record<string, string>;
}

export interface CreatePriceRequest {
  productId: string;
  unitAmount: number;
  currency: string;
  interval: 'month' | 'year';
  intervalCount?: number;
  metadata?: Record<string, string>;
}

export interface PlanOverrideRequest {
  planId: string;
  reason: string;
}

export interface TrialExtendRequest {
  additionalDays: number;
}

export interface AdminFeatureCatalog {
  version: string;
  features: Array<{
    id: FeatureId;
    label: string;
    description: string;
    category: FeatureCategory;
    dependsOn: FeatureId[];
    upgradeMessage: string;
    legacyAliases?: string[];
  }>;
}

export interface AdminPlanFeaturePolicy {
  planId: string;
  catalogVersion: string;
  version: number;
  features: Record<FeatureId, PlanFeaturePolicy>;
  updatedAt?: string;
  updatedBy?: string;
}

// ── Helper ────────────────────────────────────────────────────────────────────

// ── API ───────────────────────────────────────────────────────────────────────

export const adminBillingApi = {
  getFeatureCatalog: () =>
    adminFetch<AdminFeatureCatalog>('/mgmt/admin/features/catalog'),

  getPlanFeatures: (planId: string) =>
    adminFetch<AdminPlanFeaturePolicy>(
      `/mgmt/admin/billing/plans/${encodeURIComponent(planId)}/features`,
    ),

  updatePlanFeatures: (
    planId: string,
    expectedVersion: number,
    features: Record<FeatureId, {
      presentation: FeaturePresentation;
      defaultEnabled: boolean;
    }>,
  ) =>
    adminFetch<AdminPlanFeaturePolicy>(
      `/mgmt/admin/billing/plans/${encodeURIComponent(planId)}/features`,
      {
        method: 'PUT',
        body: JSON.stringify({ expectedVersion, features }),
      },
    ),

  getTenantFeatures: (tenantId: string) =>
    adminFetch<OrganizationFeaturesResponse>(
      `/mgmt/admin/tenants/${encodeURIComponent(tenantId)}/features`,
    ),

  updateTenantFeatures: (
    tenantId: string,
    expectedVersion: number,
    overrides: Partial<Record<FeatureId, PlatformFeatureOverride>>,
    reason: string,
  ) =>
    adminFetch<OrganizationFeaturesResponse>(
      `/mgmt/admin/tenants/${encodeURIComponent(tenantId)}/features`,
      {
        method: 'PUT',
        body: JSON.stringify({ expectedVersion, overrides, reason }),
      },
    ),

  listPlans: () =>
    adminFetch<{ plans: BillingPlan[] }>('/mgmt/admin/billing/plans').then((response) => response.plans),

  // Products
  listProducts: () =>
    adminFetch<StripeProduct[]>('/mgmt/admin/billing/products'),

  createProduct: (data: CreateProductRequest) =>
    adminFetch<StripeProduct>('/mgmt/admin/billing/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProduct: (productId: string, data: UpdateProductRequest) =>
    adminFetch<StripeProduct>(`/mgmt/admin/billing/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  archiveProduct: (productId: string) =>
    adminFetch<void>(`/mgmt/admin/billing/products/${productId}`, {
      method: 'DELETE',
    }),

  // Prices
  listPrices: () =>
    adminFetch<StripePrice[]>('/mgmt/admin/billing/prices'),

  createPrice: (data: CreatePriceRequest) =>
    adminFetch<StripePrice>('/mgmt/admin/billing/prices', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  replacePrice: (priceId: string, data: Omit<CreatePriceRequest, 'productId'>) =>
    adminFetch<StripePrice>(`/mgmt/admin/billing/prices/${priceId}/replace`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  updatePriceMetadata: (priceId: string, metadata: Record<string, string>) =>
    adminFetch<StripePrice>(`/mgmt/admin/billing/prices/${priceId}`, {
      method: 'PUT',
      body: JSON.stringify({ metadata }),
    }),

  activatePrice: (priceId: string) =>
    adminFetch<StripePrice>(`/mgmt/admin/billing/prices/${priceId}/activate`, {
      method: 'PUT',
    }),

  deactivatePrice: (priceId: string) =>
    adminFetch<StripePrice>(`/mgmt/admin/billing/prices/${priceId}/deactivate`, {
      method: 'PUT',
    }),

  // Subscriptions
  listSubscriptions: () =>
    adminFetch<AdminSubscription[]>('/mgmt/admin/billing/subscriptions'),

  getTenantSubscription: (tenantId: string) =>
    adminFetch<AdminSubscription>(`/mgmt/admin/billing/subscriptions/${tenantId}`),

  cancelSubscription: (subscriptionId: string) =>
    adminFetch<void>(`/mgmt/admin/billing/subscriptions/${subscriptionId}/cancel`, {
      method: 'PUT',
    }),

  overridePlan: (tenantId: string, data: PlanOverrideRequest) =>
    adminFetch<void>(`/mgmt/admin/billing/subscriptions/${tenantId}/override`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Stats
  getStats: () =>
    adminFetch<BillingStats>('/mgmt/admin/billing/stats'),

  // Trials
  listTrials: () =>
    adminFetch<TrialInfo[]>('/mgmt/admin/billing/trials'),

  extendTrial: (tenantId: string, data: TrialExtendRequest) =>
    adminFetch<void>(`/mgmt/admin/billing/trials/${tenantId}/extend`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  convertTrial: (tenantId: string) =>
    adminFetch<void>(`/mgmt/admin/billing/trials/${tenantId}/convert`, {
      method: 'POST',
    }),
};
